import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Shield, Loader2, CheckCircle2, AlertTriangle, XCircle, ChevronDown, ChevronUp,
  FileText, ArrowRight, RefreshCw, Trash2,
} from "lucide-react";

const STATUS_CONFIG = {
  MET: { icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", label: "Met" },
  PARTIAL: { icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50", label: "Partial" },
  NOT_MET: { icon: XCircle, color: "text-red-600", bg: "bg-red-50", label: "Not Met" },
  NOT_APPLICABLE: { icon: FileText, color: "text-slate-400", bg: "bg-slate-50", label: "N/A" },
};

export default function CrossStandardMapping() {
  const { api } = useAuth();
  const [standards, setStandards] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [expandedMapping, setExpandedMapping] = useState(null);

  useEffect(() => {
    fetchStandards();
    fetchHistory();
  }, []);

  const fetchStandards = async () => {
    try {
      const res = await api.get("/compliance-reports/standards");
      setStandards(res.data.standards || []);
    } catch { /* silent */ }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get("/compliance-reports/history?report_type=cross_standard_mapping");
      setHistory(res.data.reports || []);
    } catch { /* silent */ }
    finally { setHistoryLoading(false); }
  };

  const toggleStandard = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleGenerate = async () => {
    if (selectedIds.length === 0) { toast.error("Select at least one standard"); return; }
    setGenerating(true);
    setReport(null);
    try {
      const res = await api.post("/compliance-reports/cross-mapping", { standard_ids: selectedIds });
      setReport(res.data);
      toast.success("Cross-standard mapping report generated");
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate report");
    } finally { setGenerating(false); }
  };

  const loadReport = async (reportId) => {
    try {
      const res = await api.get(`/compliance-reports/history/${reportId}`);
      setReport({ data: res.data.report.data, ai_source: res.data.report.ai_source, title: res.data.report.title });
    } catch { toast.error("Failed to load report"); }
  };

  const deleteReport = async (reportId) => {
    try {
      await api.delete(`/compliance-reports/history/${reportId}`);
      toast.success("Report deleted");
      fetchHistory();
      if (report?.report_id === reportId) setReport(null);
    } catch { toast.error("Failed to delete"); }
  };

  const categories = {};
  standards.forEach(s => {
    if (!categories[s.category]) categories[s.category] = [];
    categories[s.category].push(s);
  });

  const mappings = report?.data?.mappings || [];

  return (
    <div className="space-y-6" data-testid="cross-standard-mapping">
      {/* Standard Selector */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg">Cross-Standard Compliance Mapping</CardTitle>
              <CardDescription>Select industry standards to map against ISO 22301 with live compliance data</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(categories).map(([cat, items]) => (
              <div key={cat}>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{cat}</p>
                <div className="flex flex-wrap gap-2">
                  {items.map(s => (
                    <button
                      key={s.id}
                      data-testid={`std-${s.id}`}
                      onClick={() => toggleStandard(s.id)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all ${
                        selectedIds.includes(s.id)
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-white text-slate-700 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50"
                      }`}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-slate-500">{selectedIds.length} standard{selectedIds.length !== 1 ? "s" : ""} selected</p>
              <Button
                onClick={handleGenerate}
                disabled={generating || selectedIds.length === 0}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="generate-mapping-btn"
              >
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                {generating ? "Generating AI Mapping..." : "Generate Compliance Mapping"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Result */}
      {report && mappings.length > 0 && (
        <div className="space-y-4" data-testid="mapping-results">
          {mappings.map((m, idx) => (
            <Card key={idx} className="border-l-4 border-l-indigo-500">
              <CardHeader className="pb-3 cursor-pointer" onClick={() => setExpandedMapping(expandedMapping === idx ? null : idx)}>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      {m.standard_name}
                      <Badge className={`text-xs ${m.overall_alignment_score >= 70 ? "bg-emerald-100 text-emerald-700" : m.overall_alignment_score >= 40 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                        {m.overall_alignment_score}% Aligned
                      </Badge>
                    </CardTitle>
                    <CardDescription className="mt-1">{m.summary}</CardDescription>
                  </div>
                  {expandedMapping === idx ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                </div>
              </CardHeader>
              {expandedMapping === idx && (
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    {(m.clause_mappings || []).map((c, ci) => {
                      const cfg = STATUS_CONFIG[c.compliance_status] || STATUS_CONFIG.PARTIAL;
                      const Icon = cfg.icon;
                      return (
                        <div key={ci} className={`${cfg.bg} rounded-lg p-4 border border-slate-100`}>
                          <div className="flex items-start gap-3">
                            <Icon className={`w-5 h-5 mt-0.5 ${cfg.color} flex-shrink-0`} />
                            <div className="flex-1 space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold text-slate-800">{c.standard_clause}</span>
                                <ArrowRight className="w-3 h-3 text-slate-400" />
                                <span className="text-sm font-medium text-indigo-700">ISO 22301 {c.iso22301_clause}</span>
                                <Badge className={`text-xs ${cfg.bg} ${cfg.color} border-0`}>{cfg.label}</Badge>
                              </div>
                              <p className="text-xs text-slate-600"><strong>Requirement:</strong> {c.standard_requirement}</p>
                              {c.evidence && <p className="text-xs text-slate-600"><strong>Evidence:</strong> {c.evidence}</p>}
                              {c.gap && c.compliance_status !== "MET" && (
                                <p className="text-xs text-red-600"><strong>Gap:</strong> {c.gap}</p>
                              )}
                              {c.recommendation && c.compliance_status !== "MET" && (
                                <p className="text-xs text-indigo-600"><strong>Recommendation:</strong> {c.recommendation}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Report History */}
      {history.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Mapping History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {history.map(r => (
                <div key={r.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex-1 cursor-pointer" onClick={() => loadReport(r.id)}>
                    <p className="text-sm font-medium text-slate-800">{r.title}</p>
                    <p className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()} &middot; {r.ai_source}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => loadReport(r.id)} data-testid={`load-mapping-${r.id}`}>
                      <RefreshCw className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => deleteReport(r.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
